/**
 * workspaceTabsStore — browser-style page tabs (opt-in).
 *
 * Enabled via VITE_WORKSPACE_TABS=true. When disabled, the provider renders
 * children as-is and useWorkspaceTabs() degrades to no-ops, so callers
 * (Navigation Cmd-click) never need to branch.
 *
 * Tab identity = route path. Tabs persist to sessionStorage so a reload keeps
 * the working set.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { labelForPath } from '../registry/portalRegistry';

export interface WorkspaceTab {
  path: string;
  label: string;
}

interface WorkspaceTabsValue {
  tabs: WorkspaceTab[];
  open: (tab: WorkspaceTab) => void;
  close: (path: string) => void;
}

const NOOP_VALUE: WorkspaceTabsValue = {
  tabs: [],
  open: () => {},
  close: () => {},
};

const STORAGE_KEY = 'portal.workspaceTabs';

export function isWorkspaceTabsEnabled(): boolean {
  return import.meta.env.VITE_WORKSPACE_TABS === 'true';
}

export function isOpenInNewTabGesture(e: React.MouseEvent): boolean {
  return e.metaKey || e.ctrlKey || e.button === 1;
}

function prettifySegment(path: string): string {
  const last = path.split('/').filter(Boolean).pop() ?? path;
  return last
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export function buildPageTab(path: string): WorkspaceTab {
  return { path, label: labelForPath(path) ?? prettifySegment(path) };
}

function loadTabs(): WorkspaceTab[] {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as WorkspaceTab[];
      if (Array.isArray(parsed)) return parsed.filter((t) => t && typeof t.path === 'string');
    }
  } catch {
    // ignore
  }
  return [];
}

function saveTabs(tabs: WorkspaceTab[]): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
  } catch {
    // ignore
  }
}

const WorkspaceTabsContext = createContext<WorkspaceTabsValue>(NOOP_VALUE);

export const WorkspaceTabsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const enabled = isWorkspaceTabsEnabled();
  const [tabs, setTabs] = useState<WorkspaceTab[]>(() => (enabled ? loadTabs() : []));

  const open = useCallback((tab: WorkspaceTab) => {
    setTabs((prev) => {
      if (prev.some((t) => t.path === tab.path)) return prev;
      const next = [...prev, tab];
      saveTabs(next);
      return next;
    });
  }, []);

  const close = useCallback((path: string) => {
    setTabs((prev) => {
      const next = prev.filter((t) => t.path !== path);
      saveTabs(next);
      return next;
    });
  }, []);

  const value = useMemo<WorkspaceTabsValue>(
    () => (enabled ? { tabs, open, close } : NOOP_VALUE),
    [enabled, tabs, open, close],
  );

  return <WorkspaceTabsContext.Provider value={value}>{children}</WorkspaceTabsContext.Provider>;
};

export function useWorkspaceTabs(): WorkspaceTabsValue {
  return useContext(WorkspaceTabsContext);
}
